import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { fetchAccordionData } from "../../../utils/faq";
import useActiveSection from "../../../utils/use-active-section";
import faqGroups from "../data/faq-groups";
import FaqItem from "./Faq-Item";
import FaqContactCard from "./Faq-Contact-Card";
import SideNav from "../../content-page/SideNav";

const FaqAccordion = ({ query = "" }) => {
  const { data, loading, error } = useSelector((state) => state.faq);
  const search = query.trim().toLowerCase();

  useEffect(() => {
    fetchAccordionData();
  }, []);

  const groups = useMemo(
    () =>
      faqGroups
        .map((group) => ({
          ...group,
          items: data.filter(
            (item) =>
              group.categories.includes(item.category) &&
              (!search ||
                `${item.question} ${item.answer}`
                  .toLowerCase()
                  .includes(search)),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [data, search],
  );

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  const [activeId, goTo] = useActiveSection(
    groups.map((group) => `faq-${group.id}`),
  );

  if (loading) {
    return <div className="faq-status">Loading...</div>;
  }

  if (error) {
    return <div className="faq-status">Error: {error}</div>;
  }

  return (
    <div className="content-page__layout">
      <SideNav
        label="FAQ topics"
        items={groups.map((group) => ({
          id: `faq-${group.id}`,
          title: group.title,
          icon: group.icon,
          count: group.items.length,
        }))}
        activeId={activeId}
        onSelect={goTo}
      />

      <div className="faq-content">
        <FaqContactCard />

        {search && total > 0 && (
          <p className="faq-results" role="status">
            {total} {total === 1 ? "result" : "results"} for “{query.trim()}”
          </p>
        )}

        {total === 0 && (
          <div className="faq-empty" role="status">
            <h2>No questions match “{query.trim()}”</h2>
            <p>
              Try a different word, or clear the search to browse all topics.
            </p>
          </div>
        )}

        {groups.map((group) => {
          const Icon = group.icon;
          return (
            <section
              key={group.id}
              id={`faq-${group.id}`}
              className="faq-group"
            >
              <header className="faq-group__header">
                <span className="faq-group__icon" aria-hidden="true">
                  <Icon />
                </span>
                <div>
                  <h2 className="faq-group__title">{group.title}</h2>
                  <p className="faq-group__description">{group.description}</p>
                </div>
              </header>
              {group.items.map((item) => (
                <FaqItem
                  key={item.id}
                  question={item.question}
                  answer={item.answer}
                />
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default FaqAccordion;
